import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { getAuth } from 'firebase/auth';
import {
  getUserProfile,
  updateUserProfile,
  addFavoriteBusiness as addFavoriteAPI,
  removeFavoriteBusiness as removeFavoriteAPI,
  trackLogin
} from '../services/userService';
import { logger } from '../utils/logger';
import { setVerticalOverride } from '../utils/verticalOverride';

const UserContext = createContext();

const DEMO_USER_CONTEXT = {
  userProfile: null,
  loading: false,
  error: null,
  isFirstLoginToday: false,
  activeVertical: null,
  justFavorited: false,
  toggleFavorite: async () => ({ isNowFavorited: false }),
  isFavorited: () => false,
  loadUserProfile: async () => {},
  setActiveVertical: () => {},
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    return DEMO_USER_CONTEXT;
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFirstLoginToday, setIsFirstLoginToday] = useState(null); // null = not yet determined
  const [activeVertical, setActiveVertical] = useState(null); // Agency vertical override
  const [justFavorited, setJustFavorited] = useState(false); // Briefly true after favoriting

  // Load user profile
  const loadUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        setUserProfile(null);
        setLoading(false);
        return;
      }

      const profile = await getUserProfile();
      setUserProfile(profile);

      // Track login event (updates streak and determines if first login today)
      trackLogin()
        .then(result => {
          if (result && typeof result.is_first_login_today === 'boolean') {
            setIsFirstLoginToday(result.is_first_login_today);
          } else {
            // Fallback to true if result is missing (first login scenario)
            setIsFirstLoginToday(true);
          }
        })
        .catch(() => {
          // Default to showing welcome on error
          setIsFirstLoginToday(true);
        });

      logger.info('User profile loaded successfully');
    } catch (err) {
      logger.error('Error loading user profile:', err);
      setError(err.message || 'Failed to load user profile');
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh user profile
  const refreshUserProfile = useCallback(async () => {
    await loadUserProfile();
  }, [loadUserProfile]);

  // Toggle favorite status (users can only have one favorite business)
  const toggleFavorite = useCallback(async (mapsUri) => {
    try {
      if (!userProfile) {
        throw new Error('User profile not loaded');
      }

      const currentFavorites = userProfile.favorite_business_uris || [];
      const isFavorited = currentFavorites.includes(mapsUri);
      const hadExistingFavorite = currentFavorites.length > 0;

      // Optimistic update - users can only have one favorite
      // If removing: empty array
      // If adding: array with only the new favorite (replaces any existing)
      const updatedFavorites = isFavorited ? [] : [mapsUri];

      setUserProfile(prev => ({
        ...prev,
        favorite_business_uris: updatedFavorites
      }));

      // API call
      if (isFavorited) {
        await removeFavoriteAPI(mapsUri);
        logger.info('Favorite business removed');
      } else {
        await addFavoriteAPI(mapsUri);
        logger.info('Business set as favorite');
      }

      // Refresh to get updated favorite businesses list
      await refreshUserProfile();

      // Signal navbar highlight when a favorite is added
      if (!isFavorited) {
        setJustFavorited(true);
        setTimeout(() => setJustFavorited(false), 3500);
      }

      // Return detailed result for UI feedback
      return {
        isNowFavorited: !isFavorited,
        wasReplacement: !isFavorited && hadExistingFavorite && !currentFavorites.includes(mapsUri),
      };
    } catch (err) {
      logger.error('Error toggling favorite:', err);
      // Revert optimistic update on error
      await refreshUserProfile();
      throw err;
    }
  }, [userProfile, refreshUserProfile]);

  // Check if a business is favorited
  const isFavorited = useCallback((mapsUri) => {
    if (!userProfile || !userProfile.favorite_business_uris) {
      return false;
    }
    return userProfile.favorite_business_uris.includes(mapsUri);
  }, [userProfile]);

  // Get display name (with fallback to first_name + last_name)
  const getDisplayName = useCallback(() => {
    if (!userProfile) return '';

    if (userProfile.display_name && userProfile.display_name.trim()) {
      return userProfile.display_name.trim();
    }

    const firstName = userProfile.first_name || '';
    const lastName = userProfile.last_name || '';

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    return userProfile.email?.split('@')[0] || '';
  }, [userProfile]);

  // Get user initials for avatar
  const getInitials = useCallback(() => {
    if (!userProfile) return '?';

    // Get display name directly instead of calling getDisplayName()
    let displayName = '';

    if (userProfile.display_name && userProfile.display_name.trim()) {
      displayName = userProfile.display_name.trim();
    } else {
      const firstName = userProfile.first_name || '';
      const lastName = userProfile.last_name || '';

      if (firstName || lastName) {
        displayName = `${firstName} ${lastName}`.trim();
      } else {
        displayName = userProfile.email?.split('@')[0] || '';
      }
    }

    if (!displayName) return '?';

    const parts = displayName.split(' ').filter(p => p);

    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }

    return '?';
  }, [userProfile]);

  // Persist activeVertical: sync to shared module and save to backend as default
  useEffect(() => {
    setVerticalOverride(activeVertical);
    if (activeVertical && userProfile?.is_agency_owner) {
      updateUserProfile({ vertical: activeVertical }).catch((err) => {
        logger.error('Failed to persist default vertical', err);
      });
    }
  }, [activeVertical, userProfile?.is_agency_owner]);

  // Load profile on mount and when auth state changes
  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        loadUserProfile();
      } else {
        setUserProfile(null);
        setIsFirstLoginToday(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [loadUserProfile]);

  // Memoized computed values
  const displayName = useMemo(() => getDisplayName(), [getDisplayName]);

  const initials = useMemo(() => getInitials(), [getInitials]);

  // Agency vertical override: activeVertical takes priority over user's default
  const effectiveVertical = useMemo(
    () => activeVertical || userProfile?.vertical || '',
    [activeVertical, userProfile]
  );

  const value = useMemo(() => ({
    userProfile,
    loading,
    error,
    refreshUserProfile,
    toggleFavorite,
    isFavorited,
    getDisplayName,
    getInitials,
    isFirstLoginToday,

    // Direct access to profile data
    email: userProfile?.email || '',
    firstName: userProfile?.first_name || '',
    lastName: userProfile?.last_name || '',
    displayName,
    initials,
    vertical: effectiveVertical,
    activeVertical,
    setActiveVertical,
    isAgencyOwner: userProfile?.is_agency_owner === true,
    agencyVerticals: userProfile?.agency_verticals || [],
    stats: userProfile?.stats || {},
    activity: userProfile?.activity || {},
    preferences: userProfile?.preferences || {},
    assignedBusinesses: userProfile?.assigned_businesses || [],
    favoriteBusinesses: userProfile?.favorite_businesses || [],
    favoriteUris: userProfile?.favorite_business_uris || [],
    agencyBranding: userProfile?.agency_branding || null,
    brandName: userProfile?.agency_branding?.agency_name || 'Localytics',
    onboardingStatus: userProfile?.onboarding_status || null,
    justFavorited,
  }), [userProfile, loading, error, refreshUserProfile, toggleFavorite, isFavorited, getDisplayName, getInitials, isFirstLoginToday, displayName, initials, effectiveVertical, activeVertical, justFavorited]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
